/* ==========================================================================
   Linkea — animaciones al hacer scroll (GSAP + ScrollTrigger)
   Atributos que se pueden poner en cualquier elemento:
     data-fx-expand="soft|normal|strong"  la imagen se expande (recorte que se abre)
     data-fx-zoom                         la imagen se aleja suavemente al entrar
     data-fx-parallax                     la imagen se mueve más lenta que la página
     data-fx-speed="0.3"                  el elemento sube a otra velocidad (negativo = más lento)
     data-fx-swing                        la cinta gira un poco al bajar
   Secciones: data-fx-scroll-expand (imagen fijada que crece a pantalla completa)
              data-fx-hgallery (galería que se desplaza de lado)
   ========================================================================== */
(function () {
  'use strict';

  var cfg = (window.LinkeaTheme && window.LinkeaTheme.scrollFx) || {};
  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger || cfg.enabled === false) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ ignoreMobileResize: true });
  document.documentElement.classList.add('fx-ready');

  var K = parseFloat(cfg.intensity) || 1;
  var CARDS = cfg.cards !== false;
  // Recorte inicial [vertical %, horizontal %] según el nivel de expansión
  var EXPAND = { soft: [5, 4], normal: [9, 7], strong: [14, 10] };

  function $(sel, scope) { return (scope || document).querySelector(sel); }
  function $$(sel, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(sel)); }
  function radius(el) { return getComputedStyle(el).borderTopLeftRadius || '0px'; }

  // Altura de la cabecera fija, para que lo que se fija al bajar no quede debajo de ella
  function headerOffset() {
    var header = $('.section-header');
    if (!header || getComputedStyle(header).position !== 'sticky') return 0;
    return header.offsetHeight;
  }
  function setHeaderVar() {
    document.documentElement.style.setProperty('--fx-header', headerOffset() + 'px');
  }
  function pinStart() { return 'top ' + headerOffset() + 'px'; }
  setHeaderVar();
  ScrollTrigger.addEventListener('refreshInit', setHeaderVar);
  function inset(v, h, r) { return 'inset(' + v + '% ' + h + '% ' + v + '% ' + h + '% round ' + r + ')'; }

  /* ---------- Efectos sueltos ---------- */
  function expand(el) {
    var level = EXPAND[el.getAttribute('data-fx-expand')] || EXPAND.normal;
    var r = radius(el);
    gsap.fromTo(el,
      { clipPath: inset(level[0] * K, level[1] * K, r) },
      {
        clipPath: inset(0, 0, r),
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'clamp(top 50%)', scrub: 1 }
      });
  }

  function zoom(el) {
    var frame = el.closest('[data-fx-expand]') || el.parentElement;
    gsap.fromTo(el,
      { scale: 1 + 0.22 * K },
      {
        scale: 1,
        ease: 'none',
        scrollTrigger: { trigger: frame, start: 'top bottom', end: 'clamp(center 45%)', scrub: 1 }
      });
  }

  function parallax(el) {
    var amount = 9 * K;
    gsap.set(el, { scale: 1 + (amount * 2 + 2) / 100 });
    gsap.fromTo(el,
      { yPercent: -amount },
      {
        yPercent: amount,
        ease: 'none',
        scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true }
      });
  }

  function speed(el) {
    var s = parseFloat(el.getAttribute('data-fx-speed')) || 0.2;
    var hero = el.closest('[data-hero]');
    if (hero) {
      // En la portada: todo empieza en su sitio y cada capa sube a su ritmo
      gsap.to(el, {
        y: function () { return -s * window.innerHeight * 0.5 * K; },
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true, invalidateOnRefresh: true }
      });
      return;
    }
    var distance = function () { return s * window.innerHeight * 0.25 * K; };
    gsap.fromTo(el,
      { y: distance },
      {
        y: function () { return -distance(); },
        ease: 'none',
        scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true, invalidateOnRefresh: true }
      });
  }

  function swing(el) {
    gsap.fromTo(el,
      { rotation: -4 * K },
      {
        rotation: 1.5 * K,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
  }

  /* ---------- Imagen que se expande a pantalla completa ---------- */
  function scrollExpand(section) {
    var frame = $('[data-sx-frame]', section);
    var media = $('[data-sx-media]', section);
    var left = $('[data-sx-left]', section);
    var right = $('[data-sx-right]', section);
    var content = $('[data-sx-content]', section);
    var hint = $('[data-sx-hint]', section);
    var length = parseFloat(section.getAttribute('data-length')) || 120;
    var mm = gsap.matchMedia();

    mm.add({ desktop: '(min-width: 750px)', mobile: '(max-width: 749px)' }, function (ctx) {
      var desktop = ctx.conditions.desktop;
      section.classList.add('is-fx');

      var tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: section,
          start: pinStart,
          end: '+=' + length + '%',
          pin: true,
          scrub: 1,
          anticipatePin: 1,
          invalidateOnRefresh: true
        }
      });

      tl.fromTo(frame,
        { clipPath: desktop ? inset(20, 31, '32px') : inset(23, 9, '24px') },
        { clipPath: inset(0, 0, '0px'), duration: 1 }, 0)
        .fromTo(media, { scale: 1 + 0.35 * K }, { scale: 1, duration: 1 }, 0)
        .fromTo(left,
          desktop ? { xPercent: 0, autoAlpha: 1 } : { yPercent: 0, autoAlpha: 1 },
          desktop ? { xPercent: -140, autoAlpha: 0, duration: 0.75 } : { yPercent: -160, autoAlpha: 0, duration: 0.75 }, 0)
        .fromTo(right,
          desktop ? { xPercent: 0, autoAlpha: 1 } : { yPercent: 0, autoAlpha: 1 },
          desktop ? { xPercent: 140, autoAlpha: 0, duration: 0.75 } : { yPercent: 160, autoAlpha: 0, duration: 0.75 }, 0)
        .fromTo(content, { autoAlpha: 0, y: 50 }, { autoAlpha: 1, y: 0, duration: 0.3 }, 0.68);
      if (hint) tl.to(hint, { autoAlpha: 0, duration: 0.15 }, 0);

      return function () { section.classList.remove('is-fx'); };
    });
    section._fxMM = mm;
  }

  /* ---------- Galería horizontal ---------- */
  function hGallery(section) {
    var track = $('[data-hgal-track]', section);
    var mm = gsap.matchMedia();

    mm.add('(min-width: 750px)', function () {
      section.classList.add('is-fx');
      var distance = function () { return Math.max(0, track.scrollWidth - window.innerWidth); };

      var move = gsap.to(track, {
        x: function () { return -distance(); },
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: pinStart,
          end: function () { return '+=' + distance(); },
          pin: true,
          scrub: 1,
          invalidateOnRefresh: true,
          anticipatePin: 1
        }
      });

      $$('[data-hgal-frame]', section).forEach(function (frame) {
        var r = radius(frame);
        gsap.fromTo(frame,
          { clipPath: inset(12 * K, 4 * K, r) },
          {
            clipPath: inset(0, 0, r),
            ease: 'none',
            scrollTrigger: { trigger: frame, containerAnimation: move, start: 'left right', end: 'center 55%', scrub: true }
          });
      });
      $$('[data-hgal-inner]', section).forEach(function (inner) {
        gsap.fromTo(inner,
          { scale: 1 + 0.3 * K },
          {
            scale: 1,
            ease: 'none',
            scrollTrigger: { trigger: inner.parentElement, containerAnimation: move, start: 'left right', end: 'right left', scrub: true }
          });
      });

      return function () { section.classList.remove('is-fx'); };
    });
    section._fxMM = mm;
  }

  /* ---------- Arranque ---------- */
  var EFFECTS = [
    { sel: '[data-fx-scroll-expand]', fn: scrollExpand },
    { sel: '[data-fx-hgallery]', fn: hGallery },
    { sel: '[data-fx-expand]', fn: expand, card: true },
    { sel: '[data-fx-zoom]', fn: zoom, card: true },
    { sel: '[data-fx-parallax]', fn: parallax },
    { sel: '[data-fx-speed]', fn: speed },
    { sel: '[data-fx-swing]', fn: swing }
  ];

  function init(root) {
    EFFECTS.forEach(function (fx) {
      $$(fx.sel, root).forEach(function (el) {
        if (el._fxDone) return;
        if (fx.card && !CARDS && el.closest('[data-fx-card]')) return;
        // Los elementos dentro de la vista rápida o los cajones no se animan
        if (el.closest('[data-drawer]')) return;
        el._fxDone = true;
        fx.fn(el);
      });
    });
  }

  // Quita las animaciones de elementos que ya no existen (filtros, editor de temas...)
  function prune() {
    ScrollTrigger.getAll().forEach(function (st) {
      var t = st.trigger;
      if (t && !document.documentElement.contains(t)) st.kill();
    });
  }

  var refresh = (function () {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(function () { ScrollTrigger.refresh(); }, 120);
    };
  })();

  init(document);

  // Contenido nuevo cargado por theme.js (filtros, productos relacionados...)
  document.addEventListener('linkea:content', function (e) {
    prune();
    init((e.detail && e.detail.root) || document);
    refresh();
  });
  // Algo cambió de tamaño o de visibilidad (pestañas, etc.)
  document.addEventListener('linkea:layout', refresh);

  // Editor de temas de Shopify
  document.addEventListener('shopify:section:load', function (e) {
    prune();
    init(e.target);
    refresh();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    $$('[data-fx-scroll-expand], [data-fx-hgallery]', e.target).forEach(function (el) {
      if (el._fxMM) el._fxMM.revert();
    });
    setTimeout(function () { prune(); refresh(); }, 0);
  });

  // Las imágenes que tardan en cargar pueden cambiar la altura de la página
  window.addEventListener('load', refresh);
})();
